import './action-modals.scss';
import { FwVersionInfo } from '../../../../../dtos/version.dto';
import { observer } from 'mobx-react-lite';
import { useStores } from '../../../../store';
import usePromiseFeedback from '../../../../shared/promise-feedback/usePromiseFeedback';
import React, { useEffect, useMemo, useState } from 'react';
import CustomIcon, { IconType } from '../../../../shared/custom-icon/custom-icon';
import GenericModal from '../../../../shared/generic-modal/generic-modal';
import { CopyAction, DisableReasons, PolicyType } from '../../../../../utils/enums';
import { Modal } from 'antd';
import { ApplyingPolicyModalContent } from '../../../dashboard/policy-modal/applying-policy-modal-content';
import { EventType } from '../../../../../dtos/analytics.dto';
import { MultiSelectConnections } from '../../../../shared/multi-select-connections/multi-select-connections';

interface PushPolicyModalProps {
    open: boolean;
    close: () => void;
    closePanel: () => void;
    sourceCircuitId?: string;
    versionData?: FwVersionInfo;
}

export const PushPolicyModal: React.FC<PushPolicyModalProps> = observer(({
    open,
    close,
    sourceCircuitId,
    versionData,
    closePanel
}) => {
    const { customerStore, fwVersionStore } = useStores();

    const [promiseFeedbackFactory, promiseContextHolder] = usePromiseFeedback();
    const [isModalVisible, setIsModalVisible] = useState(true);
    const [selectedConnections, setSelectedConnections] = useState<number[]>([]);
    const [actionInProgress, setActionInProgress] = useState<CopyAction>();
    const [isApplyPolicyDisplay, setIsApplyPolicyDisplay] = useState(false);

    const notSelectedError = useMemo(() =>
            !selectedConnections.length && !!actionInProgress ? 'This field is required' : undefined
    , [selectedConnections, actionInProgress]);

    useEffect(() => {
        setActionInProgress(undefined);
    }, [selectedConnections]);

    useEffect(() => {
        if (open) {
            setIsModalVisible(true);
            setActionInProgress(undefined);
            setSelectedConnections([]);
        }
    }, [open, fwVersionStore]);

    const closeModal = () => {
        setActionInProgress(undefined);
        setSelectedConnections([]);
        setIsModalVisible(false);
        close();
    };

    const onVersionCopyAsDraft = () => {
        setActionInProgress(CopyAction.CopyAsDraft);
        if (!selectedConnections.length) return;

        fwVersionStore.sendGeneralEvent(EventType.LinkClicked, { button: 'copyAsDraft' });
        promiseFeedbackFactory({
            promise: fwVersionStore.pushPolicy(customerStore.selectedCustomer?.customerId, versionData?.sid, selectedConnections, false),
            successCallback: () => {
                closeModal();
                closePanel();
            },
            errorCallback: () => setActionInProgress(undefined),
            notifObj: {
                show: true,
                notificationMsg: `Policy V${versionData?.versionNumber} is successfully copied as draft to ${selectedConnections.length} connections.`
            }
        });
    };

    const onVersionCopyAndApply = () => {
        setActionInProgress(CopyAction.CopyAndApply);
        if (!selectedConnections.length) return;

        fwVersionStore.sendGeneralEvent(EventType.LinkClicked, { button: 'copyAndApply' });
        promiseFeedbackFactory({
            promise: fwVersionStore.pushPolicy(customerStore.selectedCustomer?.customerId, versionData?.sid, selectedConnections, true),
            successCallback: () => {
                setIsApplyPolicyDisplay(true);
            },
            errorCallback: () => setActionInProgress(undefined),
            notifObj: {show: false}
        });
    };

    const onCopyApplyGotIt = () => {
        setIsApplyPolicyDisplay(false);
        closeModal();
        closePanel();
    };

    const copyPolicyModalBody = useMemo(() => {
        let versionInfo = `V${versionData?.versionNumber}`;
        if (versionData?.versionName) {
            versionInfo += ` - ${versionData.versionName}`;
        }

        return <>
            <div className={'policy-action-modal-description'}>Origin</div>
            <div className={'policy-action-modal-details'}>
                <div><CustomIcon type={IconType.IP_CONTROL}/>{sourceCircuitId}</div>
                <div>
                    <CustomIcon type={IconType.EMPTY_SHIELD}/>{versionInfo}
                </div>
            </div>
            <MultiSelectConnections label={'Copy to'} sourceSid={versionData?.sid}
                                    selectedConnections={selectedConnections} setSelectedConnections={setSelectedConnections}
                                    error={notSelectedError} disabled={selectedConnections.length > 0 && !!actionInProgress} />
        </>;
    }, [customerStore.selectedLocation, versionData, selectedConnections, notSelectedError, actionInProgress]);

    const isSelectedConnectionDeactivated = useMemo(() =>
            selectedConnections.some(conn =>
                fwVersionStore.allFwVersions[conn]?.[0]?.versionType === PolicyType.DEACTIVATE
            )
        , [selectedConnections]);

    const actionButtons = [
        {
            text: CopyAction.CopyAsDraft,
            onClick: () => {
                onVersionCopyAsDraft();
            },
            disabled: selectedConnections.length > 0 && (!!actionInProgress || isSelectedConnectionDeactivated),
            disableReason: isSelectedConnectionDeactivated ? DisableReasons.CREATE_DRAFT_NOT_AVAILABLE_DEACTIVATED : '',
            className: `secondary-btn ${actionInProgress === CopyAction.CopyAsDraft && selectedConnections.length ? 'loading' : ''}`
        },
        {
            text: CopyAction.CopyAndApply,
            onClick: () => {
                onVersionCopyAndApply();
            },
            disabled: selectedConnections.length > 0 && !!actionInProgress,
            className: `primary-btn ${actionInProgress === CopyAction.CopyAndApply && selectedConnections.length ? 'loading' : ''}`,
        }
    ];

    return open ? <>
        <Modal open={isApplyPolicyDisplay} className={`policy-modal apply`} width={680} closable={false}
                footer={null} centered>
            <ApplyingPolicyModalContent onClickGotIt={onCopyApplyGotIt}
                message={`${selectedConnections.length} connections are now being applied. ` +
                    `It may take a few minutes until activated. You will be notified by email.`}/>
        </Modal>
        <GenericModal open={isModalVisible}
            width={600}
            body={copyPolicyModalBody}
            onCancel={closeModal}
            title={'Copy Policy'}
            actionButtons={actionButtons}
            wrapClassName={'push-policy-modal'} />
        {promiseContextHolder}
    </> : null;
});